import { EventDrivenStore } from '@core/store/eventDrivenStore';
import constants from '@core/utils/constants';

class Store extends EventDrivenStore {
  constructor() {
    super('cart', {
      cart: null,
      loading: false,
      error: null,
      // NEW - Order fees calculation state
      orderFees: null,
      orderFeesLoading: false,
      orderFeesError: null
    });

    this.setupEvents();
  }

  setupEvents() {
    // ============ LOAD EVENTS ============
    this.on(constants.EVENTS.CART.LOADING, () => {
      this.state.loading = true;
      this.state.error = null;
    });

    this.on(constants.EVENTS.CART.LOADED, (cart) => {
      this.state.cart = cart;
      this.state.loading = false;
      this.state.error = null;
    });

    this.on(constants.EVENTS.CART.LOAD_ERROR, (error) => {
      this.state.loading = false;
      this.state.error = error?.message || error;
    });

    // ============ ADD ITEM EVENTS ============
    this.on(constants.EVENTS.CART.ITEMS.ADDING, () => {
      this.state.loading = true;
      this.state.error = null;
    });

    this.on(constants.EVENTS.CART.ITEMS.ADDED, (cart) => {
      this.state.cart = cart;
      this.state.loading = false;
      this.state.error = null;
    });

    this.on(constants.EVENTS.CART.ITEMS.ADD_ERROR, (error) => {
      this.state.loading = false;
      this.state.error = error?.message || error;
    });

    // ============ UPDATE ITEM EVENTS ============
    this.on(constants.EVENTS.CART.ITEMS.UPDATING, () => {
      this.state.loading = true;
      this.state.error = null;
    });

    this.on(constants.EVENTS.CART.ITEMS.UPDATED, (cart) => {
      this.state.cart = cart;
      this.state.loading = false;
      this.state.error = null;
    });

    this.on(constants.EVENTS.CART.ITEMS.UPDATE_ERROR, (error) => {
      this.state.loading = false;
      this.state.error = error?.message || error;
    });

    // ============ REMOVE ITEM EVENTS ============
    this.on(constants.EVENTS.CART.ITEMS.REMOVING, () => {
      this.state.loading = true;
      this.state.error = null;
    });

    this.on(constants.EVENTS.CART.ITEMS.REMOVED, (cart) => {
      this.state.cart = cart;
      this.state.loading = false;
      this.state.error = null;
    });

    this.on(constants.EVENTS.CART.ITEMS.REMOVE_ERROR, (error) => {
      this.state.loading = false;
      this.state.error = error?.message || error;
    });

    // ============ CLEAR CART EVENTS ============
    this.on(constants.EVENTS.CART.CLEARING, () => {
      this.state.loading = true;
      this.state.error = null;
    });

    this.on(constants.EVENTS.CART.CLEARED, () => {
      this.state.cart = null;
      this.state.loading = false;
      this.state.error = null;
    });

    this.on(constants.EVENTS.CART.CLEAR_ERROR, (error) => {
      this.state.loading = false;
      this.state.error = error?.message || error;
    });

    // ============ NEW - OPTIONS UPDATE EVENTS ============
    this.on(constants.EVENTS.CART.ITEMS.OPTIONS.UPDATING, () => {
      this.state.loading = true;
      this.state.error = null;
    });

    this.on(constants.EVENTS.CART.ITEMS.OPTIONS.UPDATED, (cart) => {
      this.state.cart = cart;
      this.state.loading = false;
      this.state.error = null;
    });

    this.on(constants.EVENTS.CART.ITEMS.OPTIONS.UPDATE_ERROR, (error) => {
      this.state.loading = false;
      this.state.error = error?.message || error;
    });

    // ============ NEW - DRINKS UPDATE EVENTS ============
    this.on(constants.EVENTS.CART.ITEMS.DRINKS.UPDATING, () => {
      this.state.loading = true;
      this.state.error = null;
    });

    this.on(constants.EVENTS.CART.ITEMS.DRINKS.UPDATED, (cart) => {
      this.state.cart = cart;
      this.state.loading = false;
      this.state.error = null;
    });

    this.on(constants.EVENTS.CART.ITEMS.DRINKS.UPDATE_ERROR, (error) => {
      this.state.loading = false;
      this.state.error = error?.message || error;
    });

    // ============ NEW - GLOBAL DRINKS UPDATE EVENTS ============
    this.on(constants.EVENTS.CART.DRINKS.UPDATING, () => {
      this.state.loading = true;
      this.state.error = null;
    });

    this.on(constants.EVENTS.CART.DRINKS.UPDATED, (cart) => {
      this.state.cart = cart;
      this.state.loading = false;
      this.state.error = null;
    });

    this.on(constants.EVENTS.CART.DRINKS.UPDATE_ERROR, (error) => {
      this.state.loading = false;
      this.state.error = error?.message || error;
    });

    // ============ NEW - ORDER FEES CALCULATION EVENTS ============
    this.on(constants.EVENTS.CART.ORDER_FEES_CALCULATING, () => {
      this.state.orderFeesLoading = true;
      this.state.orderFeesError = null;
    });

    this.on(constants.EVENTS.CART.FEES_CALCULATED, (fees) => {
      this.state.orderFees = fees;
      this.state.orderFeesLoading = false;
      this.state.orderFeesError = null;

      // Update cart fees if cart exists
      if (this.state.cart) {
        this.state.cart.deliveryFee = fees.deliveryFee || 0;
        this.state.cart.originalDeliveryFee = fees.originalDeliveryFee || fees.deliveryFee || 0;
        this.state.cart.deliveryPromotion = fees.deliveryPromotion || null;
        // Recalculate total if needed (or assume total from fees is correct)
        this.state.cart.total = (this.state.cart.subtotal || 0) + 
                                (fees.deliveryFee || 0) + 
                                (this.state.cart.serviceFee || 0) + 
                                (this.state.cart.paymentFee || 0) - 
                                (this.state.cart.discount || 0);
      }
    });

    this.on(constants.EVENTS.CART.ORDER_FEES_CALCULATED, (fees) => {
      this.state.orderFees = fees;
      this.state.orderFeesLoading = false;
      this.state.orderFeesError = null;
    });

    this.on(constants.EVENTS.CART.ORDER_FEES_CALCULATE_ERROR, (error) => {
      this.state.orderFeesLoading = false;
      this.state.orderFeesError = error?.message || error;
    });

    // ============ COUPON EVENTS ============
    this.on(constants.EVENTS.CART.COUPON.APPLYING, () => {
      this.state.loading = true;
      this.state.error = null;
    });

    this.on(constants.EVENTS.CART.COUPON.APPLIED, (cart) => {
      this.state.cart = cart;
      this.state.loading = false;
      this.state.error = null;
    });

    this.on(constants.EVENTS.CART.COUPON.APPLY_ERROR, (error) => {
      this.state.loading = false;
      this.state.error = error?.message || error;
    });

    this.on(constants.EVENTS.CART.COUPON.REMOVING, () => {
      this.state.loading = true;
      this.state.error = null;
    });

    this.on(constants.EVENTS.CART.COUPON.REMOVED, (cart) => {
      this.state.cart = cart;
      this.state.loading = false;
      this.state.error = null;
    });

    this.on(constants.EVENTS.CART.COUPON.REMOVE_ERROR, (error) => {
      this.state.loading = false;
      this.state.error = error?.message || error;
    });
  }

  // Getters
  get items() {
    return this.state.cart?.items || [];
  }

  get total() {
    return this.state.cart?.total || 0;
  }

  get subtotal() {
    return this.state.cart?.subtotal || 0;
  }

  get deliveryFee() {
    return this.state.cart?.deliveryFee || 0;
  }

  get originalDeliveryFee() {
    return this.state.cart?.originalDeliveryFee || this.deliveryFee;
  }

  get deliveryPromotion() {
    return this.state.cart?.deliveryPromotion || null;
  }

  get serviceFee() {
    return this.state.cart?.serviceFee || 0;
  }

  get discount() {
    return this.state.cart?.discount || 0;
  }

  get itemCount() {
    return this.items.length
    return this.items.reduce((sum, item) => sum + item.quantity, 0);
  }

  get restaurantId() {
    return this.state.cart?.restaurantId;
  }

  get restaurantName() {
    return this.state.cart?.restaurantName;
  }

  get isEmpty() {
    return !this.state.cart || this.items.length === 0;
  }

  get isLoading() {
    return this.state.loading;
  }

  get hasError() {
    return !!this.state.error;
  }

  // NEW - Order fees getters
  get orderFees() {
    return this.state.orderFees;
  }

  get orderFeesLoading() {
    return this.state.orderFeesLoading;
  }

  get orderFeesError() {
    return this.state.orderFeesError;
  }

  get deliveryDistance() {
    return this.state.orderFees?.distanceKm || 0;
  }

  get deliveryDuration() {
    return this.state.orderFees?.durationMin || 0;
  }
}

export default Store;
